/*import { useBand } from '../providers/band.provider'*/
import { createEffect, createSignal, onCleanup } from 'solid-js'
import { NumericalDataType } from '../models/numerical-data-type'

export type DecimalFormatterFunc = (value?: number, numFractionDigits?: number) => string | undefined
export type CurrencyFormatterFunc = (
  value?: number,
  numFractionDigits?: number,
  currency?: string
) => string | undefined
export type CurrencyWithSignFormatterFunc = (
  value?: number,
  numFractionDigits?: number,
  currency?: string
) => string | undefined
export type NumberWithSignFormatterFunc = (value?: number, numFractionDigits?: number) => string | undefined
export type NumericalFormatterFunc = (
  type: NumericalDataType,
  value?: number,
  numFractionDigits?: number
) => string | undefined

export interface currencyFormatterResult {
  readonly currencyFormatter: CurrencyFormatterFunc
  readonly currencyWithSignFormatter: CurrencyWithSignFormatterFunc
  readonly numberFormatter: (value: number, numFractionDigits?: number) => string | undefined
  readonly numberWithSignFormatter: NumberWithSignFormatterFunc
  readonly percentageFormatter: DecimalFormatterFunc
  readonly percentageWithSignFormatter: DecimalFormatterFunc
  readonly formatNumericValue: NumericalFormatterFunc
  readonly shortCurrencyFormatter: CurrencyFormatterFunc
}

export const useNumberFormatter = (): {
  formatNumericValue: (
    type: NumericalDataType,
    value: number | undefined,
    numFractionDigits?: number
  ) => string | undefined
  currencyFormatter: (value?: number, numFractionDigits?: number, currency?: string) => string
  currencyWithSignFormatter: (value?: number, numFractionDigits?: number, currency?: string) => string
  numberFormatter: (value?: number, numFractionDigits?: number) => string | undefined
  numberWithSignFormatter: (value?: number, numFractionDigits?: number) => string | undefined
  percentageFormatter: (value: number | undefined, numFractionDigits?: number) => string | undefined
  percentageWithSignFormatter: (value: number | undefined, numFractionDigits?: number) => string | undefined
  shortCurrencyFormatter: (value?: number, numFractionDigits?: number, currency?: string) => string
} => {
  const [locale, setLocale] = createSignal<string>()
  /*const { selectedBand } = useBand()*/

  const bandCurrency = (): string => /*selectedBand()?.currencyCode  ??*/ 'USD'

  const handleLanguageChange = (): void => {
    setLocale(window.navigator.language ?? 'en-US')
  }

  window.addEventListener('languagechange', handleLanguageChange)
  onCleanup(() => {
    window.removeEventListener('languagechange', handleLanguageChange)
  })

  createEffect(() => {
    setLocale(window.navigator.language ?? 'en-US')
  })

  const currencyFormatter = (value?: number, numFractionDigits: number = 2, currency?: string): string => {
    const currencyCode = currency ?? bandCurrency()
    return new Intl.NumberFormat(locale(), {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 2,
      minimumFractionDigits: numFractionDigits
    }).format(value!)
  }

  const currencyWithSignFormatter = (value?: number, numFractionDigits: number = 0, currency?: string): string => {
    if (value === undefined || value === null) return ''
    const sign = value > 0 ? '+' : value < 0 ? '-' : ''
    const absValue = Math.abs(value)
    const currencyCode = currency ?? bandCurrency()
    const formatted = new Intl.NumberFormat(locale(), {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: numFractionDigits,
      minimumFractionDigits: numFractionDigits
    }).format(absValue)
    return `${sign}${formatted}`
  }

  const shortCurrencyFormatter = (value?: number, numFractionDigits: number = 2, currency?: string): string => {
    if (value == null) return ''

    const code = currency ?? bandCurrency()
    const abs = Math.abs(value)
    const localeId = locale()

    if (abs >= 1e6) {
      return new Intl.NumberFormat(localeId, {
        style: 'currency',
        currency: code,
        notation: 'compact',
        compactDisplay: 'short',
        minimumFractionDigits: numFractionDigits,
        maximumFractionDigits: numFractionDigits
      }).format(value)
    }

    if (abs >= 1e4) {
      return new Intl.NumberFormat(localeId, {
        style: 'currency',
        currency: code,
        notation: 'compact',
        compactDisplay: 'short',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)
    }

    return currencyFormatter(value, numFractionDigits, currency)
  }

  const numberFormatter = (value?: number, numFractionDigits: number = 0): string | undefined => {
    if (value === undefined) {
      return
    }

    return Intl.NumberFormat(locale(), { maximumFractionDigits: 2, minimumFractionDigits: numFractionDigits }).format(
      value
    )
  }

  const numberWithSignFormatter = (value?: number, numFractionDigits: number = 0): string | undefined => {
    if (value === undefined || value === null) {
      return
    }
    const sign = value > 0 ? '+' : value < 0 ? '-' : ''
    const absValue = Math.abs(value)
    const formatted = Intl.NumberFormat(locale(), {
      maximumFractionDigits: numFractionDigits,
      minimumFractionDigits: numFractionDigits
    }).format(absValue)
    return `${sign}${formatted}`
  }

  const percentageFormatter = (value: number | undefined, numFractionDigits: number = 0): string | undefined => {
    if (value === undefined) {
      return undefined
    }

    return `${numberFormatter(value * 100, numFractionDigits)}%`
  }

  const percentageWithSignFormatter = (
    value: number | undefined,
    numFractionDigits: number = 1
  ): string | undefined => {
    if (value === undefined || value === null) {
      return undefined
    }
    const sign = value > 0 ? '+' : value < 0 ? '-' : ''
    const absValue = Math.abs(value * 100)
    return `${sign}${numberFormatter(absValue, numFractionDigits)}%`
  }

  const formatNumericValue = (
    type: NumericalDataType,
    value: number | undefined,
    numFractionDigits?: number
  ): string | undefined => {
    switch (type) {
      case NumericalDataType.Currency:
        return currencyFormatter(value, numFractionDigits)
      case NumericalDataType.Percentage:
        return percentageFormatter(value, numFractionDigits)
      default:
        return numberFormatter(value, numFractionDigits)
    }
  }

  return {
    currencyFormatter,
    currencyWithSignFormatter,
    numberFormatter,
    numberWithSignFormatter,
    percentageFormatter,
    percentageWithSignFormatter,
    formatNumericValue,
    shortCurrencyFormatter
  }
}

/* eslint-disable no-extend-native */
declare global {
  export interface Number {
    isPositive: () => boolean
    isNegative: () => boolean
  }
}

Number.prototype.isPositive = function (): boolean {
  return +this > 0
}

Number.prototype.isNegative = function (): boolean {
  return +this < 0
}
